import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120POLInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1120-POL Instructions | Political Organization Filing"
          description="Learn how to e-file Form 1120-POL with step-by-step instructions, filing deadlines, extensions, penalties, taxable income rules, and IRS filing requirements."
          keywords=""
          canonical="resources/tax-exempt/form-1120-pol-instructions/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 1120-POL </span>
          </span>{' '}
          Political Organizations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file your political organization’s Form 1120-POL with TaxZerone and
          stay IRS compliant.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polinstructions`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-1120-pol-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1120-POL at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 1120-POL?{' '}
                        </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-1120-pol/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 1120-POL
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          U.S. Income Tax Return for Certain Political
                          Organizations,
                        </strong>{' '}
                        is used to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          political organization taxable income and related
                          federal income tax under Section 527.{' '}
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File? A political organization
                        </strong>{' '}
                        must generally file Form 1120-POL when it has{' '}
                        <strong style={{ fontWeight: 600 }}>
                          political organization taxable income exceeding the
                          $100 specific deduction.
                        </strong>{' '}
                        Certain{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Section 501(c) organizations{' '}
                        </strong>
                        may also have to file when they are treated as having
                        political organization taxable income under Section
                        527(f)(1).
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 1120-POL generally reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          taxable investment and other non-exempt-function
                          income,
                        </strong>{' '}
                        allowable deductions directly connected with earning
                        that income, and the resulting{' '}
                        <strong style={{ fontWeight: 600 }}>
                          political organization taxable income and tax
                          liability.
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Other Forms May Be Required?
                        </strong>{' '}
                        Depending on the organization's filing obligations,{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 8871, Form 8872,
                        </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          990
                        </Link>
                        /{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-ez-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          990-EZ
                        </Link>
                        , <strong style={{ fontWeight: 600 }}>Form 8997</strong>{' '}
                        or other applicable forms may also be required.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Typography variant="body1" sx={{ mb: 4 }}>
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 1120-POL
                  </Link>{' '}
                  is an IRS tax return used by political organizations to report
                  political organization taxable income and to determine any
                  federal income tax due. Using this form involves reporting all
                  taxable income, calculating the tax due, and revealing
                  information on political organization activities that qualify
                  for federal tax. Whether your political organization is a PAC,
                  campaign committee, political party, or any other political
                  organization, knowledge of Form 1120-POL filing requirements
                  is necessary for maintaining compliance.
                </Typography> */}

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents{' '}
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 1120-POL?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Must File Form 1120-POL?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing Deadline for Form 1120-POL
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalties" sx={{ color: '#0202ff' }}>
                          Penalties for Late Filing or Late Payment
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form1120pol" sx={{ color: '#0202ff' }}>
                          Form 1120-POL Instructions
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#mail" sx={{ color: '#0202ff' }}>
                          Where to mail Form 1120-POL?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What’s New for Form 1120-POL?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS encourages political organizations to use electronic
                  payment and direct deposit options for faster, safer, and more
                  convenient processing of Form 1120-POL.
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 4,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Payments{' '}
                    </span>{' '}
                    <br />
                    Organizations with access to U.S. banking services can{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      make IRS tax payments electronically{' '}
                    </span>{' '}
                    whenever possible.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Making a Payment </span>{' '}
                    <br />
                    If there is a tax balance due on Part III, line 24, payments
                    can be made through the IRS electronic payment system.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Direct Deposit for Refunds
                    </span>{' '}
                    <br />
                    If there is an overpayment reported on Part III, line 25,
                    organizations can receive refunds through direct deposit by{' '}
                    <Link
                      href="/resources/tax-exempt/form-8050-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      filing Form 8050
                    </Link>{' '}
                    with their banking information. Direct deposits are
                    generally the fastest way to receive an IRS refund.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  What is Form 1120-POL?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1120-POL
                  </Link>
                  ,{' '}
                  <span style={{ fontStyle: 'italic' }}>
                    {' '}
                    “U.S. Income Tax Return for Certain Political
                    Organizations,"
                  </span>{' '}
                  is an IRS tax form used by political organizations to report
                  their taxable income and to figure out their federal income
                  tax, if any, for the tax year. Generally, this form should be
                  filed by political parties, campaign committees, political
                  action committees (PACs), and similar political organizations.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Despite the fact that most political organizations receive
                  contributions that are not taxable, there is a possibility
                  that some types of incomes might be taxed according to IRS
                  requirements. Thus, filling Form 1120-POL will help you in
                  reporting your taxable income and claiming deductions.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          mb: 1,
                          color: '#f8f9fa',
                          fontSize: 17,
                          lineHeight: 1.8,
                        }}
                      >
                        File your Form 1120-POL quickly and securely with
                        TaxZerone and enjoy 10% off — use code{' '}
                        <Box
                          component="span"
                          sx={{
                            p: '3px 8px',
                            backgroundColor: '#faedcd',
                            color: '#ff006f',
                            letterSpacing: 2,
                            border: '2px dashed #212121',
                            fontWeight: 600,
                          }}
                        >
                          {' '}
                          TZOTE10
                        </Box>{' '}
                        at checkout!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polinstructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who Must File Form 1120-POL?
                </Typography>
                <Typography variant="body1">
                  Political organizations that have any taxable income during
                  the tax year must fill out <br /> Form 1120-POL, including:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    '& li': {
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <Box>Political parties </Box>
                  </li>

                  <li>
                    {' '}
                    <Box>Political committees </Box>
                  </li>
                  <li>
                    {' '}
                    <Box>Campaign committees </Box>
                  </li>
                  <li>
                    {' '}
                    <Box>PACs </Box>
                  </li>
                  <li>
                    {' '}
                    <Box>
                      Associations and other similar organizations primarily
                      influence elections and appointments.{' '}
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Nonpolitical organizations classified as tax-exempt but not as
                  political organizations must also fill out this form, as they
                  are subject to income tax under{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/#difference"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Section 527(f)(1)
                  </Link>
                  . Some income is considered taxable if it doesn't qualify as
                  exempt function
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    '& li': {
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <Box>Investment income </Box>
                  </li>
                  <li>
                    {' '}
                    <Box>Advertising revenue, or </Box>
                  </li>
                  <li>
                    {' '}
                    <Box>Other revenue received by the organization </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Political organizations filing Form 1120-POL may also be
                  required to file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ
                  </Link>{' '}
                  to report annual information to the IRS.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="filing"
                >
                  Filing Deadline for Form 1120-POL
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Political organizations must generally{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1120-POL
                  </Link>{' '}
                  by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    15th day of the 4th month
                  </span>{' '}
                  following the end of their tax year. For{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    calendar-year organizations
                  </span>
                  , the usual filing deadline is{' '}
                  <span style={{ fontWeight: 600 }}> April 15</span>. If the due
                  date falls on a Saturday, Sunday, or legal holiday, the return
                  may be filed on the next business day.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If there are organizations that need more time to file, they
                  can apply for an{' '}
                  <span style={{ fontWeight: 600 }}> automatic extension</span>{' '}
                  with the help of{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 7004
                  </Link>
                  . It is necessary to note that the extension does not mean
                  extending the payment deadline. The tax due should be paid
                  regardless of the filing period.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#f8f9fa',
                          fontSize: 17,
                        }}
                      >
                        Need more time to file your political organization form?
                      </Typography>
                      <Typography variant="body1">
                        E-file Form 7004 with the IRS through TaxZerone and
                        request an automatic extension in just a few simple
                        steps- fast, secure, and completed online within
                        minutes.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polinstructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="penalties"
                >
                  Penalties for Late Filing or Late Payment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Late filing or late payment of the tax due may lead to
                  penalties and interest charges from the IRS. Generally, the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    late filing penalty is 5%
                  </span>{' '}
                  of the tax for each month of delay and the{' '}
                  <span style={{ fontWeight: 600 }}> maximum 25%</span> of the
                  tax due.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the 1120-POL form is filed more than{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    60 days after the due date,
                  </span>{' '}
                  the IRS may charge a minimum late filing penalty equal to the
                  smaller of the unpaid tax or $525.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, when the organization fails to pay the tax on the
                  deadline, the IRS can impose a late payment penalty of{' '}
                  <span style={{ fontWeight: 600 }}> 0.5%</span> of the amount
                  per month or part until the balance is paid, which cannot
                  exceed{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    25% of the unpaid tax
                  </span>
                  . Penalties may also be imposed if there is negligence,
                  substantial underpayment of tax, or fraud.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0,
                        fontSize: '24px !important',
                      }}
                    >
                      Avoid IRS Penalties for Late Filing
                    </Typography>
                    <Typography variant="body2">
                      Avoid late filing and payment penalties by submitting Form
                      1120-POL on time. TaxZerone makes e-filing simple, secure,
                      and IRS-compliant for political organizations.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 200 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        mb: 0,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polinstructions`}
                    >
                      E-file Form 1120-POL
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="form1120pol"
                >
                  Form 1120-POL Instructions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use the instructions below to learn how to complete the form
                  below and{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1120-POL online
                  </Link>{' '}
                  for a secure and IRS-compliant filing experience.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Organization Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  First, you need to fill in information about the organization
                  such as political organization name, address, employer
                  identification number (EIN), and tax year. You must also
                  provide details related to the type of political organization
                  and filing status before completing the income and tax
                  sections of the form.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-pol-organise-img.webp" />
                  <source srcSet="/assets/images/f1120-pol-organise-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-pol-organise-img.png"
                    loading="lazy"
                    alt="Form 1120-POL, U.S. Income Tax Return for Certain Political Organizations-Organization Information"
                    width={760}
                    height={206}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Tax Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s tax year in this section. For those
                  organizations that operate on a calendar year basis, the tax
                  year shall start on{' '}
                  <span style={{ fontWeight: 600 }}> January 1, 2025</span>, and
                  end on{' '}
                  <span style={{ fontWeight: 600 }}> December 31, 2025</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In the case of fiscal-year organizations, one needs to enter
                  the starting and ending date of the tax year. Ensure the dates
                  entered match the organization’s accounting records and IRS
                  filing period.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The 2025
                  Form 1120-POL can also be used for short tax years that begin
                  and end in 2026 if the 2026 version of the form is not yet
                  available when the return is due. In such cases, organizations
                  must enter their 2026 tax year information on the 2025 form
                  and apply any tax law changes effective for tax years
                  beginning after 2025.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Check if this is a section 501(c) organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Mark this checkbox if the political organization is associated
                  with a tax-exempt organization under{' '}
                  <span style={{ fontWeight: 600 }}> Section 501(c)</span>. This
                  helps the IRS identify organizations that may have political
                  organization taxable income under{' '}
                  <span style={{ fontWeight: 600 }}> Section 527(f)(1)</span>.
                  Additional details about Section 527(f)(1) are available on
                  our{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1120-POL form
                  </Link>{' '}
                  Instructions page.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Check if
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select the appropriate box that applies to the return being
                  filed:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Final return -</span>{' '}
                    Check this box if the organization is closing or no longer
                    required to file Form 1120-POL.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Name change -</span>{' '}
                    Select this if the organization’s legal name has changed
                    since the last filing.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Address change -</span>{' '}
                    Check this box if the organization’s mailing address has
                    changed.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Amended return -</span>{' '}
                    Check this box if you are amending an existing Form
                    1120-POL.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The
                  business must notify the IRS if its address has changed since
                  the filing of Form 1120-POL by submitting{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f8822b.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8822-B
                  </Link>
                  , Change of Address or Responsible Party - Business.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Name of organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the legal name of the political organization registered
                  with the IRS.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Employer Identification Number (EIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the organization’s valid EIN. This number is used to
                  identify the organization for federal tax purposes.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Number and street / P.O. box
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s mailing address, including the street
                  address or P.O. box number.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Room or suite number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the room, suite, or office number if applicable to the
                  mailing address.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  City, State or Province, Country, and ZIP or Foreign Postal
                  Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the complete information about city, state/province,
                  country, and ZIP code/foreign postal code of the organization.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.8,
                  }}
                >
                  Part I - Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-pol-part1-income-img.webp" />
                  <source srcSet="/assets/images/f1120-pol-part1-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-pol-part1-income-img.png"
                    loading="lazy"
                    alt="Form 1120-POL, Part I - Income Line 1 to Line 8"
                    width={760}
                    height={146}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this section, the political organizations must disclose
                  their taxable income according to IRS reporting rules. Most of
                  the campaign contributions and exempt function income are not
                  taxable income. Income from sources that are not associated
                  with exempt political activities may be disclosed in Form
                  1120-POL.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Dividends
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include any{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    income received in dividends
                  </span>{' '}
                  by the political organization in that tax year. Provide a
                  statement indicating sources of dividend income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate all taxable{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    interest income received
                  </span>{' '}
                  from banks, investments, or other sources generating interest
                  income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3 - Gross Rents
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State the{' '}
                  <span style={{ fontWeight: 600 }}> total rental income</span>{' '}
                  received from rental properties in that tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4 - Gross Royalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    royalty income received
                  </span>{' '}
                  from copyrights, trademarks, licensing agreements, or similar
                  sources.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5 - Capital Gain Net Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s{' '}
                  <span style={{ fontWeight: 600 }}> net capital gains</span>.
                  Attach{' '}
                  <Link
                    href="/resources/tax-exempt/schedule-d-form-1120-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule D (Form 1120)
                  </Link>{' '}
                  if required.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Net Gain or (Loss) from Form 4797
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Gains or losses</span>{' '}
                  realized from the disposition of business property must be
                  reported and, where appropriate,{' '}
                  <Link
                    href="/resources/tax-exempt/form-4797-sales-business-property/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 4797
                  </Link>{' '}
                  must be attached.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7 - Other income and nonexempt function expenditures
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Other income taxable from other sources, including income
                    from trade or business, advertising income, exempt function
                    income not properly segregated, or income subject to tax
                    because of late filing of Form 8871.
                  </li>
                  <li>
                    Also include expenditures made from exempt function income
                    that were not used for exempt political activities,
                    including certain nonexempt or illegal expenditures. Attach
                    a detailed statement listing all amounts included on this
                    line.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8 - Total Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add lines 1 through 7 and enter the total taxable income for
                  the organization.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  Part II – Deductions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-pol-part2-deductions-img.webp" />
                  <source srcSet="/assets/images/f1120-pol-part2-deductions-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-pol-part2-deductions-img.png"
                    loading="lazy"
                    alt="Form 1120-POL, Part II - Deduction Line 9 to Line 18"
                    width={760}
                    height={211}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Salaries and Wages
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Salaries, wages, and other employee payments made in
                  connection with taxable income generating activities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Repairs and Maintenance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Expenses incurred for{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    repairs and maintenance
                  </span>{' '}
                  of taxable income producing property or activities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11 - Rents
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Rental expenses </span>{' '}
                  incurred for the use of offices, machinery, or other property
                  used in the generation of taxable income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 12 - Taxes and licenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tax-deductible taxes, licenses, and fees incurred during the
                  taxable year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13 - Interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Interest expenses that were incurred due to activities
                  connected with the generation of taxable income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 14 - Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Deductions for depreciation of expense on property that is
                  used in business.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 15 - Other Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    other allowable deductions
                  </span>{' '}
                  directly connected with earning taxable income. Attach a
                  supporting statement with details.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 16 - Total Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 9 through 15 and enter the total deductions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17 - Taxable Income Before Specific Deduction of $100
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract total deductions (
                  <span style={{ fontWeight: 600 }}>line 16</span>) from total
                  income (<span style={{ fontWeight: 600 }}>line 8</span>) to
                  calculate taxable income before the specific deduction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For Section 501(c) organizations that are not political
                  organizations:
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter net investment income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount spent for exempt political activities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17c
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the smaller of line 17a or line 17b.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18 - Specific Deduction of $100
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Political organizations may claim a specific deduction of
                  $100. However, this deduction is not allowed for newsletter
                  funds described under{' '}
                  <span style={{ fontWeight: 600 }}> Section 527(g)</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  Part III - Tax and Payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-pol-part3-tax-img.webp" />
                  <source srcSet="/assets/images/f1120-pol-part3-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-pol-part3-tax-img.png"
                    loading="lazy"
                    alt="Form 1120-POL, Part III - Tax and Payments Line 19 to Line 25"
                    width={760}
                    height={244}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 19 - Taxable Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Enter the organization’s taxable income
                  </span>{' '}
                  after subtracting the $100 specific deduction from line 18. If
                  the taxable{' '}
                  <span style={{ fontWeight: 600 }}>
                    income in line 19 is zero or less,
                  </span>{' '}
                  Form 1120-POL is generally not required to be filed. However,
                  organizations may still choose to file the return to begin the
                  IRS statute of limitations period.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 20 - Income Tax
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Form 1120-POL income is taxed at a flat rate of 21%.
                    Multiply the taxable income on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      line 19 by 21% (0.21)
                    </span>{' '}
                    and enter the result on this line.
                  </li>
                  <li>
                    If Form 8978 was filed, attach it to the return, but do not
                    include amounts from Form 8978, line 14, on this line.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Estimated
                  tax payments and alternative minimum tax generally do not
                  apply to political organizations filing Form 1120-POL
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21 - Tax Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Political organizations may claim certain eligible tax credits
                  in this section.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any allowable foreign tax credits and attach Form 1118
                  if applicable.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report other eligible credits, such as the employer credit for
                  paid family and medical leave or qualified electric vehicle
                  credit carryforwards.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter general business credits from{' '}
                  <Link
                    href="/resources/tax-exempt/form-3800-business-credits/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 3800
                  </Link>
                  , excluding credits that are not allowed for Form 1120-POL
                  filers.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add all applicable credits and enter the total amount. Attach
                  all supporting credit forms.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 22 - Total Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total credits on line 21
                  </span>{' '}
                  from the{' '}
                  <span style={{ fontWeight: 600 }}>
                    income tax amount reported on line 20
                  </span>{' '}
                  to determine the organization’s total tax liability. Include
                  any qualified electric vehicle credit recapture amount on this
                  line, if applicable.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23 - Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter all payments and credits already made toward the
                  organization’s tax liability.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tax paid with Form 7004 extension request
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Credit for tax paid on undistributed capital gains (attach
                  Form 2439)
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Credit for federal tax on fuels (attach Form 4136)
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Elective payment election amount from Form 3800 for Section
                  527 organizations
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23e
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Total payments and credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach all applicable forms supporting the payments and
                  credits claimed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 24 - Tax Due
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the total tax on line 22 is more than the payments reported
                  on line 23e, enter the remaining balance due on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS requires federal tax payments to be made
                  electronically. Organizations unable to pay the full amount
                  immediately may apply for an IRS online payment agreement to
                  pay in installments.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 25 - Overpayment
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If your organization has access to U.S. banking services,
                  direct deposit is the fastest way to receive IRS refunds. If
                  there is an overpayment when filing Form 1120-POL, complete
                  and attach{' '}
                  <Link
                    href="/resources/tax-exempt/form-8050-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8050
                  </Link>{' '}
                  to provide the organization’s banking information for direct
                  deposit.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Signature Section
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-pol-sign-img.webp" />
                  <source srcSet="/assets/images/f1120-pol-sign-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-pol-sign-img.png"
                    loading="lazy"
                    alt="Form 1120-POL, Officer Signature and Details of Paid Preparer"
                    width={760}
                    height={128}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part of the tax form is to ensure that the information
                  entered on Form 1120-POL is correct and complete. A signature
                  is required from an authorized officer of the political
                  organization and a paid tax preparer if one is hired.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Signature of Officer
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The signature of an authorized officer of the political
                  organization (president, vice-president, treasurer, assistant
                  treasurer, or tax officer) is required. By signing, the
                  authorized officer confirms that the information provided on
                  the return is true, accurate, and complete to the best of
                  their knowledge.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Date and Title
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the return is signed along with the official
                  title or designation of the authorized signer within the
                  organization.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  May the IRS discuss this return with the Preparer?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}> “Yes”</span> if the
                  organization authorizes the IRS to discuss the filed return
                  with the paid preparer listed in this section. Otherwise,
                  check <span style={{ fontWeight: 600 }}>“No.”</span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paid Preparer Use Only
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the return is prepared by a paid tax professional, the
                  preparer must complete this section by providing:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Preparer’s name</li>
                  <li>Signature </li>
                  <li>Date </li>
                  <li>PTIN (Preparer Tax Identification Number) </li>
                  <li>Firm’s name </li>
                  <li>Firm’s EIN </li>
                  <li>Firm’s address </li>
                  <li>Phone number </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If the return is prepared by an employee of the organization
                  and no payment was made for preparation services, this section
                  should generally be left blank.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IV - Additional Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-pol-part4-add-img.webp" />
                  <source srcSet="/assets/images/f1120-pol-part4-add-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-pol-part4-add-img.png"
                    loading="lazy"
                    alt="Form 1120-POL, Part IV - Additional Information Line 26 to Line 30"
                    width={760}
                    height={218}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 26 - Foreign Financial Accounts
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Check <span style={{ fontWeight: 600 }}>“Yes”</span> if the
                    organization had a financial interest in, signature
                    authority over, or other authority over foreign financial
                    accounts during the calendar year and the combined value of
                    those accounts{' '}
                    <span style={{ fontWeight: 600 }}>exceeded $10,000</span> at
                    any time during the year.
                  </li>
                  <li>
                    Organizations must also check{' '}
                    <span style={{ fontWeight: 600 }}>“Yes”</span> if they own{' '}
                    <span style={{ fontWeight: 600 }}> more than 50%</span> of a
                    corporation that holds such foreign financial accounts.
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes”</span> is
                    selected, the organization may be required to electronically
                    file <span style={{ fontWeight: 600 }}>FinCEN</span> Form
                    114 (FBAR) with the U.S. Department of the Treasury. This
                    form is filed separately and should not be attached to the
                    1120-POL Form. The organization must also enter the name of
                    the foreign country or countries associated with the
                    accounts.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 27 - Foreign Trusts and Foreign Gifts
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the organization answers{' '}
                    <span style={{ fontWeight: 600 }}>“Yes”</span> to line 27,
                    it may be required to file Form 3520, Annual Return to
                    Report Transactions with Foreign Trusts and Receipt of
                    Certain Foreign Gifts.{' '}
                  </li>
                  <li>
                    Organizations that own a foreign trust may also need to
                    ensure that Form 3520-A is filed annually for the
                    trust.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 28 - Tax-Exempt Interest Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any tax-exempt interest income received or accrued
                  during the tax year. This includes exempt-interest dividends
                  received from mutual funds or other regulated investment
                  companies.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 29 - Date Organization Formed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Enter the date</span> the
                  political organization was officially formed or established.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 30a - The Books Are in Care Of
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    name of the individual
                  </span>{' '}
                  responsible for maintaining the organization’s financial books
                  and records.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 30b - Enter Name of Candidate
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    name of the political candidate
                  </span>{' '}
                  associated with the organization.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 30c - The Books Are Located At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the address where the organization’s books and
                  financial records are maintained.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 30d - Telephone Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}> contact number</span> for
                  the individual or office responsible for maintaining the
                  organization’s records.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="mail"
                >
                  Where to mail Form 1120-POL?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Political organizations filing Form 1120-POL by paper mail
                  should send the return to the following address:
                </Typography>
                <Box
                  sx={{
                    maxWidth: 300,
                    mb: 3,
                    p: 2,
                    border: '5px solid transparent',
                    borderRadius: 3,
                    background:
                      'linear-gradient(#fcf6bd) padding-box, repeating-linear-gradient(-45deg, #C8102E 0, #C8102E 12.5%, #ffffff 0, #ffffff 25%, #3492b8 0, #3492b8 37.5%, #ffffff 0, #ffffff 50%) 0 / 5em 5em',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                    {' '}
                    <MailOutlineOutlinedIcon
                      sx={{
                        position: 'relative',
                        ml: 0,
                        mr: 0.5,
                        mb: -1,
                        fontSize: 28,
                      }}
                    />{' '}
                  </Typography>

                  <Box
                    component="div"
                    sx={{ textAlign: 'left', fontSize: '15px important' }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      Department of the Treasury
                      <br />
                      Internal Revenue Service Center
                      <br />
                      Ogden, UT 84201
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization’s principal office or agency is located in
                  a foreign country or a U.S. territory, mail the return to:
                </Typography>
                <Box
                  sx={{
                    maxWidth: 300,
                    mb: 3,
                    p: 2,
                    border: '5px solid transparent',
                    borderRadius: 3,
                    background:
                      'linear-gradient(#fcf6bd) padding-box, repeating-linear-gradient(-45deg, #C8102E 0, #C8102E 12.5%, #ffffff 0, #ffffff 25%, #3492b8 0, #3492b8 37.5%, #ffffff 0, #ffffff 50%) 0 / 5em 5em',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                    {' '}
                    <MailOutlineOutlinedIcon
                      sx={{
                        position: 'relative',
                        ml: 0,
                        mr: 0.5,
                        mb: -1,
                        fontSize: 28,
                      }}
                    />{' '}
                  </Typography>

                  <Box
                    component="div"
                    sx={{ textAlign: 'left', fontSize: '15px important' }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      Internal Revenue Service Center
                      <br />
                      P.O. Box 409101
                      <br />
                      Ogden, UT 84409
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Organizations may also use IRS-designated private delivery
                  services (PDSs) to meet the timely filing requirement for
                  paper returns. Since private delivery services cannot deliver
                  to P.O. boxes, the IRS provides separate street addresses for
                  <Link
                    href="https://www.irs.gov/filing/private-delivery-services-pds"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Private Delivery Services (PDS)
                  </Link>{' '}
                  filings. Always verify the latest mailing and delivery
                  instructions on the official IRS website before filing.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on the official IRS{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1120pol.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Instructions
                        </Link>{' '}
                        for Form 1120-POL. For the most accurate and up-to-date
                        information, refer to the official IRS website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to E-file your Form 1120-POL?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Stay compliant with IRS requirements for political organizations.
              <Hidden smDown>
                {' '}
                <br />{' '}
              </Hidden>{' '}
              TaxZerone makes it simple, accurate, and secure to e-file your
              Form 1120-POL online.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polinstructions`}
              >
                E-file Form 1120-POL
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120POLInstructions;
